import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { GameMenu, ISchedule, Planner, Prescriptions, StatusMonitor, Timer } from '../Game';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../../hooks/useDialog';
import { Level, level1 } from '../../domain/level';
import { Grandma, GrandmaTickResult } from '../../domain/grandma';
import { createSchedule, PillSchedule, Schedule } from '../../domain/schedule';
import { useDiary } from '../Game/Diary/DiaryDialog';
import { useWinDialog } from '../Game/WinDialog/WinDialog';
import clsx from 'clsx';
import { useTutorial } from '../Game/Tutorial';
import { Pill } from '../../domain/status';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Pills } from '../Game/Pills/Pills';
import { useLoseDialog } from '../Game/LoseDialog/LoseDialog';
import { DragOrigin } from '../Game/Pills/Pill';
import backgroundImage from '../../assets/images/background.png';
import diaryImage from '../../assets/images/diary.png';

const useStyles = createUseStyles({
    root: {
        display: 'grid',
        gridTemplate: '"title title" "prescriptions timer" "prescriptions timer" "prescriptions status" "planner status" / 75%',
        gap: '1rem',
        '& > *': {
            border: '1px solid transparent',
            padding: '0.5rem',
        },
        backgroundImage: `url(${backgroundImage})}`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        width: '100%',
        height: '100%',
    },
    title: {
        gridArea: 'title',
        margin: 0,
    },
    prescriptions: {
        gridArea: 'prescriptions',
    },
    timer: {
        gridArea: 'timer',
    },
    planner: {
        gridArea: 'planner',
    },
    status: {
        gridArea: 'status',
    },
    diaryButton: {
        position: 'fixed',
        bottom: '0rem',
        width: '100%',
        height: '6rem',
        verticalAlign: 'top',
        backgroundImage: `url(${diaryImage})}`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundColor: 'transparent',
        border: 'none',
        '&:hover': {
            height: '8rem',
        },
    },
});

export interface IGameViewProps {
    level: Level;
    onShowMenu(): void;
}

export const gameViewKey = 'game';

const mergeSchedule = (schedule: Schedule, pillSchedule: PillSchedule): Schedule =>
    schedule.map(s => ({
        ...s,
        effects: [ ...s.effects, ...(pillSchedule[s.time] ?? []) ],
    }));

export const GameView: React.FunctionComponent<IGameViewProps> = props => {
    const styles = useStyles();
    const { t } = useTranslation();

    const [ currentDayIndex, setCurrentDayIndex ] = useState(0);
    const [ grandma, setGrandma ] = useState(new Grandma());
    const generator = useRef<Generator<GrandmaTickResult, void, unknown>>();
    const [ started, setStarted ] = useState(false);
    const [ done, setDone ] = useState(false);
    const [ autoStepping, setAutoStepping ] = useState(false);
    const autoStepInterval = useRef(0);
    const [ currentTime, setCurrentTime ] = useState(0);
    const [ tickResults, setTickResults ] = useState<Record<number, GrandmaTickResult>>({});
    const [ availablePills, setAvailablePills ] = useState<Array<Pill & { amount: number}>>([]);
    const scheduleRef = useRef<ISchedule>(null);

    useTutorial(props.level.key === level1.key ? { key: 'tutorial' } : props.level);

    const reset = useCallback((level: Level) => {
        setCurrentDayIndex(0);
        setCurrentTime(0);
        setGrandma(new Grandma());
        setDone(false);
        setTickResults({});
        setAvailablePills(level.availablePills);
        scheduleRef.current?.reset();

    }, []);

    useEffect(() => {
        reset(props.level);
    }, [ props.level, reset ]);

    const currentDay = props.level.days[currentDayIndex];
    const schedule = useMemo(() => {
        return createSchedule(currentDay.definitions);
    }, [ currentDay.definitions ]);

    const [ openMenu ] = useDialog({
        children: <GameMenu onShowMenu={props.onShowMenu} />,
    });

    const [ openDiary ] = useDiary(grandma.diary);

    const [ openWinDialog ] = useWinDialog(grandma.score, currentDayIndex + 1, props.onShowMenu);

    const [ openLoseDialog ] = useLoseDialog(currentDayIndex + 1, props.onShowMenu, () => reset(props.level));

    useEffect(() => {
        const listener = (ev: KeyboardEvent) => {
            if (ev.key === 'Escape') {
                const hasOpenModals = [ ...document.querySelectorAll('dialog') ]
                    .some(d => d.open);
                if (hasOpenModals) {
                    return;
                }
                window.setTimeout(openMenu, 10);
            }
        };
        window.addEventListener('keydown', listener);
        return () => window.removeEventListener('keydown', listener);
    }, [ openMenu ]);

    const handleStart = (pillSchedule: PillSchedule) => {
        const mergedSchedule = mergeSchedule(schedule, pillSchedule);
        grandma.simulate(mergedSchedule);
        generator.current = grandma.tick();
        setStarted(true);
        setDone(false);
        setTickResults({});
        window.setTimeout(() => {
            setAutoStepping(true);
            autoStepInterval.current = window.setInterval(() => handleStep(), 400);
        }, 1000);
    };

    const handleStep = () => {
        try {
            const generatorResult = generator.current?.next();
            setCurrentTime(grandma.time);
            if (generatorResult?.done) {
                setCurrentDayIndex(i => Math.min(i + 1, props.level.days.length - 1));
                if (currentDayIndex === props.level.days.length - 1) {
                    window.setTimeout(openWinDialog, 100);
                }
                setStarted(false);
                setAutoStepping(false);
                window.clearInterval(autoStepInterval.current);
                scheduleRef.current?.reset();
                setDone(true);
            } else if (generatorResult) {
                const value = generatorResult.value;
                setTickResults(rs => ({ ...rs, [value.time]: value }));
            }
        } catch (err) {
            setStarted(false);
            setAutoStepping(false);
            window.clearInterval(autoStepInterval.current);
            window.setTimeout(openLoseDialog, 100);
        }
    };

    const pauseAutoStep = () => {
        setAutoStepping(false);
        window.clearInterval(autoStepInterval.current);
    };

    const startAutoStep = () => {
        setAutoStepping(true);
        autoStepInterval.current = window.setInterval(() => handleStep(), 400);
    };

    const showDiary = () => {
        openDiary();
        console.log(grandma.diary);
    };

    const handlePutPack = useCallback((pill: Pill) => {
        setAvailablePills(ps => ps.map(p => p.key === pill.key ? { ...p, amount: p.amount + 1 } : p));
    }, []);

    const removePillFromOrigin = (pill: Pill, origin: DragOrigin) => {
        if (origin === 'box') {
            setAvailablePills(ps => ps.map(p => p.key === pill.key ? { ...p, amount: Math.max(0, p.amount - 1) } : p));
        } else {
            scheduleRef.current?.removePillFromSlot(pill, origin);
        }
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div className={styles.root}>
                <h1 className={styles.title}>{t('title')} - {t(`level.${props.level.key}.name`)}</h1>
                <div className={styles.timer}>
                    <Timer
                        day={currentDay}
                        time={currentTime}
                    />
                </div>
                <div className={styles.prescriptions}>
                    <Prescriptions
                        availablePills={props.level.availablePills}
                    />
                    <Pills
                        availablePills={availablePills}
                        onPutPack={handlePutPack}
                        removePillFromOrigin={removePillFromOrigin}
                    />
                </div>
                <div className={clsx(styles.status, 'status-monitor')}>
                    <StatusMonitor status={grandma.state}/>
                </div>
                <div className={styles.planner}>
                    <Planner
                        onStart={handleStart}
                        schedule={schedule}
                        currentTime={currentTime}
                        availablePills={props.level.availablePills}
                        disabled={started}
                        statusEffects={tickResults}
                        removePillFromOrigin={removePillFromOrigin}
                        ref={scheduleRef}
                        started={started}
                    />
                    {/*started && (
                        <>
                            <button onClick={handleStep}>Step</button>
                            {autoStepping ? (
                                <button onClick={pauseAutoStep}>Pause</button>
                            ) : (
                                <button onClick={startAutoStep}>Autoplay</button>
                            )}
                        </>
                    )*/}
                </div>
                <button className={styles.diaryButton} onClick={showDiary} id="diarytrigger">Diary</button>
            </div>
        </DndProvider>
    );
};
